import { Link } from '@inertiajs/react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import type { FaqItem } from '@/components/marketing/FaqAccordion'
import type { SharedMarketingProps } from '@/types/marketing'
import { useState } from 'react'

interface HelpCategory {
  title: string
  items: FaqItem[]
}

interface HelpPageProps extends SharedMarketingProps {
  categories: HelpCategory[]
}

export default function Help({ categories }: HelpPageProps) {
  const [activeCategory, setActiveCategory] = useState(0)

  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Support</span>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>Help Center</h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="hero-body mx-auto">
              Find answers to common questions about music distribution, pricing, royalties, and more.
            </p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* Category tabs */}
      <section className="container mx-auto px-6 py-6">
        <ScrollReveal>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat, i) => (
              <button
                key={cat.title}
                onClick={() => setActiveCategory(i)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === i
                    ? 'bg-[#ff6a1a] text-white'
                    : 'border border-white/10 text-white/64 hover:text-white'
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* FAQ sections */}
      <section className="marketing-section marketing-section-warm pb-20 pt-12">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <h2 className="marketing-section-title marketing-section-title-dark mb-8">
                {categories[activeCategory]?.title}
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <FaqAccordion items={categories[activeCategory]?.items || []} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="container mx-auto px-6 pb-24 pt-16">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">Still have questions?</h2>
            <p className="mt-3 text-white/64">
              Our support team responds within 12 hours on weekdays via email or Instagram.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link href="/contact-us" className="marketing-button-primary" prefetch>
                Contact Us
              </Link>
              <a href="mailto:help@wavbee.com" className="marketing-button-secondary">
                Email Support
              </a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
